import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { StorageService } from './storage.service';

/**
 * Service to handle the app state
 */
@Injectable({ providedIn: 'root' })
/**
 * AppService
 * @class AppService
 */
export class AppService {
  /**
   * Inject StorageService
   * @type {StorageService}
   */
  private storageService = inject(StorageService);
  /**
   * currentTheme
   * @type {string}
   */
  public currentTheme = 'light';
  /**
   * currentSideBarStatus$
   * @type {BehaviorSubject<boolean>}
   */
  private currentSideBarStatus$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  /**
   * currentSideBarIndex$
   * @type {BehaviorSubject<number>}
   */
  private currentSideBarIndex$: BehaviorSubject<number> = new BehaviorSubject(0);
  /**
   * currentActiveTheme$
   * @type {BehaviorSubject<'light' | 'dark'>}
   */
  private currentActiveTheme$: BehaviorSubject<'light' | 'dark'> = new BehaviorSubject('light');


  /**
   * Set the current sidebar status
   * @param status boolean
   */
  setCurrentSideBarStatus(status: boolean): void {
    this.currentSideBarStatus$.next(status);
  }

  /**
   * Return the current sidebar status
   * @returns {Observable<boolean>}
   */
  SubCurrentSideBarStatus(): Observable<boolean> {
    return this.currentSideBarStatus$;
  }

  /**
   * Set the current sidebar index
   * @param status - The index to set for the current sidebar
   */
  setCurrentSideBarIndex(status: number): void {
    this.currentSideBarIndex$.next(status);
  }

  /**
   * Return the current sidebar index
   * @returns {Observable<number>}
   */
  SubCurrentSideBarIndex(): Observable<number> {
    return this.currentSideBarIndex$;
  }

  /**
   * Set the current active theme.
   * Updates the current theme, notifies subscribers of the change,
   * and persists the theme using the StorageService.
   * @param theme - The theme to set ('light' or 'dark')
   */
  setCurrentActiveTheme(theme) {
    this.currentTheme = theme;
    this.currentActiveTheme$.next(theme);
    this.storageService.setTheme(theme);
  }

  /**
   * Return the current active theme.
   * @returns {Observable<'light' | 'dark'>}
   */
  SubCurrentActiveTheme(): Observable<'light' | 'dark'> {
    return this.currentActiveTheme$;
  }
}
/**
 * Interface for menu items
 * @interface MenuItem
 */

export interface MenuItem {

  /**
   * The key of the menu item
   * @type {string}
   */
  key: string;
  /**
   * class
   * @type {string}
   */
  class?: string;
  /**
   * The link of the menu item
   * @type {string}
   */
  link?: string;
  /**
   * The icon of the menu item
   * @type {string}
   */
  icon?: string;
  /**
   * The children of the menu item
   * @type {MenuItem[]}
   */
  children?: MenuItem[];
  /**
   * The permission of the menu item
   * @type {string[]}
   */
  userPermission?: string[];
  /**
   * The permission of the menu item
   * @type {string[]}
   */
  companyPermission?: string[];
  /**
   * The order of the menu item
   * @type {number}
   */
  order?: number;
}
